/* eslint-disable react/no-multi-comp */
// The picker is split into a thin <Popover> wrapper and a <PickerPanel> body
// so the body unmounts together with the popover content when the user closes
// it. That remount is what gives us the "search/page reset on reopen" behavior
// for free — there is no parent-owned search/page state to keep in sync.
//
// `useEntityValues` (wrapping `useQuery`) owns the fetch lifecycle; the panel
// owns `page` + `search` + accumulation. `useInfiniteQuery` is intentionally
// not used because the lego Combobox calls `onLoadMore("", 1)` from a mount
// effect keyed on the callback identity — any time the callback's deps
// changed (e.g. the active query's `isFetching` toggled) it would re-fire and
// clobber the user's in-flight search.
import { FC, useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  DebouncedInput,
  LoadingButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Stack,
  Typography,
} from "@altimateai/lego";
import {
  IconChevronDown,
  IconSearch,
  IconSquare,
  IconSquareCheckFilled,
} from "@tabler/icons-react";
import { OTHER } from "@/pages/query/common";
import { WorkflowType } from "@/pages/query/types";
import { useEntityValues } from "./useEntityValues";

export type { EntityValuesPage } from "./useEntityValues";

const PAGE_SIZE = 50;

export interface EntityLegendPickerProps {
  groupBy: string;
  startDate: Date;
  endDate: Date;
  workflowType: WorkflowType;
  value: string[];
  onChange: (values: string[]) => void;
}

interface PickerPanelProps {
  groupBy: string;
  startDate: Date;
  endDate: Date;
  workflowType: WorkflowType;
  initialValue: string[];
  onApply: (values: string[]) => void;
  onClear: () => void;
}

const formatTriggerLabel = (value: string[]): string => {
  if (value.length === 0) return "Show: All";
  if (value.length <= 2) return value.join(", ");
  return `${value[0]}, ${value[1]}, +${value.length - 2} more`;
};

const PickerPanel: FC<PickerPanelProps> = ({
  groupBy,
  startDate,
  endDate,
  workflowType,
  initialValue,
  onApply,
  onClear,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set(initialValue));

  const { data, isFetching } = useEntityValues({
    groupBy,
    startDate,
    endDate,
    workflowType,
    page,
    size: PAGE_SIZE,
    search: searchValue || undefined,
  });

  // Append/replace exactly once per fetched response.
  //
  // With `placeholderData: keepPreviousData`, `data` keeps pointing at the
  // *previous* query's result while a new page is in flight. If we keyed this
  // effect on `page`, clicking "See more" would re-run it with stale data and
  // append the previous page again before the new one arrived. Gating on the
  // data reference ensures we only act when a fresh response actually lands.
  //
  // We branch on `data.page` (not the local `page` state) so that the response
  // dictates replace vs append — that way a new search (page reset to 1)
  // cleanly replaces accumulated results when its first page arrives.
  const lastAppliedDataRef = useRef<typeof data>(undefined);
  useEffect(() => {
    if (!data || lastAppliedDataRef.current === data) return;
    lastAppliedDataRef.current = data;
    if (data.page === 1) {
      setAccumulated(data.values);
    } else {
      setAccumulated(prev => [...prev, ...data.values]);
    }
  }, [data]);

  const handleSearch = useCallback((term: string) => {
    setSearchValue(term);
    setPage(1);
  }, []);

  const toggle = useCallback((v: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  }, []);

  const totalPages = data ? Math.ceil(data.total / data.size) : 1;
  const hasMore = page < totalPages;
  const showInitialSpinner = isFetching && accumulated.length === 0;
  // OTHER is a synthetic "all unselected entities" bucket — surface it only on
  // the unfiltered list so a search for entity names doesn't return it as a
  // confusing fake match.
  const renderOtherOption = !searchValue;

  return (
    <Command shouldFilter={false} className="al-w-64">
      <Stack justify="between" align="center" className="al-p-2 al-border-b">
        <Typography>Show</Typography>
        <Button size="sm" onClick={() => onApply(Array.from(selected))}>
          Apply
        </Button>
      </Stack>
      <div className="al-p-2">
        <DebouncedInput
          debounceMs={300}
          placeholder="Search entities..."
          onChange={handleSearch}
          icon={IconSearch}
        />
      </div>
      <CommandList className="al-max-h-72 al-overflow-y-auto">
        {showInitialSpinner ? (
          <Stack justify="center" align="center" className="al-p-4">
            <Spinner size="md" />
          </Stack>
        ) : (
          <>
            <CommandEmpty>No matches found.</CommandEmpty>
            <CommandGroup>
              {renderOtherOption && (
                <CommandItem
                  value={OTHER}
                  onSelect={() => toggle(OTHER)}
                  aria-label={OTHER}
                  className="al-cursor-pointer"
                >
                  <Stack align="center" gap="sm">
                    {selected.has(OTHER) ? (
                      <IconSquareCheckFilled className="al-size-4 al-text-primary" />
                    ) : (
                      <IconSquare className="al-size-4 al-text-muted-foreground" />
                    )}
                    <Typography size="sm">{OTHER}</Typography>
                  </Stack>
                </CommandItem>
              )}
              {accumulated.map(v => (
                <CommandItem
                  key={v}
                  value={v}
                  onSelect={() => toggle(v)}
                  aria-label={v}
                  className="al-cursor-pointer"
                >
                  <Stack align="center" gap="sm">
                    {selected.has(v) ? (
                      <IconSquareCheckFilled className="al-size-4 al-text-primary" />
                    ) : (
                      <IconSquare className="al-size-4 al-text-muted-foreground" />
                    )}
                    <Typography size="sm" className="al-truncate">
                      {v}
                    </Typography>
                  </Stack>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
      {hasMore && (
        <div className="al-p-1 al-border-t">
          <LoadingButton
            size="sm"
            variant="outline"
            className="al-w-full"
            onClick={() => setPage(p => p + 1)}
            loading={isFetching}
          >
            See next {PAGE_SIZE} of {data?.total ?? 0}
          </LoadingButton>
        </div>
      )}
      <div className="al-border-t al-p-2">
        <Button size="sm" variant="outline" className="!al-h-8 al-w-full" onClick={onClear}>
          Clear Selection
        </Button>
      </div>
    </Command>
  );
};

export const EntityLegendPicker: FC<EntityLegendPickerProps> = ({
  groupBy,
  startDate,
  endDate,
  workflowType,
  value,
  onChange,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-label="Show: All"
          aria-expanded={open}
          className="al-w-56 al-justify-between al-py-3 al-px-2"
        >
          <span className="al-flex-1 al-min-w-0 al-text-left al-truncate">
            {formatTriggerLabel(value)}
          </span>
          <IconChevronDown className="al-ml-2 al-size-4 al-shrink-0 al-opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="!al-p-0 al-min-w-56" align="end">
        <PickerPanel
          groupBy={groupBy}
          startDate={startDate}
          endDate={endDate}
          workflowType={workflowType}
          initialValue={value}
          onApply={values => {
            onChange(values);
            setOpen(false);
          }}
          onClear={() => {
            onChange([]);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
